import { GRAPHQL_TYPENAMES } from 'modules/graphql/types';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { match } from 'ts-pattern';
import { ToastMessage } from '../components/ToastMessage';
import { CATEGORY_LIMIT_ERROR } from '../constants';
import { getManageMessageCategoriesNames } from '../modals/ManageCustomCategoriesModal/ManageCustomCategoriesModal.utils';
import { pluralizeCategory } from '../utils';
import {
  BeeManageMessageCategoriesMutationFailure,
  ManageMessageCategoriesOptions,
  useManageMessageCategories,
} from './useManageMessageCategories';

const UPSERT_MESSAGE_CATEGORY_ERROR_MESSAGE = 'Something went wrong. Please try again.';

const failureToErrorMessage = (failure: BeeManageMessageCategoriesMutationFailure) => {
  return match(failure)
    .with(
      {
        __typename: GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_MAXIMUM_COUNT_EXCEEDED_FAILURE,
      },
      () => CATEGORY_LIMIT_ERROR,
    )
    .with(
      {
        __typename: GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_NAME_TOO_LONG_FAILURE,
      },
      () => 'Category name exceeds limit of 40 characters.',
    )
    .with(
      {
        __typename: GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_NAME_ALREADY_EXISTS_FAILURE,
      },
      () => 'Category name already exists.',
    )
    .with(
      {
        __typename: GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_NAME_EMPTY_FAILURE,
      },
      () => 'Category name cannot be empty.',
    )
    .with(
      {
        __typename: GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_UPDATE_FAILURE,
      },
      () => 'Failed to update category.',
    )
    .with(
      {
        __typename: GRAPHQL_TYPENAMES.MESSAGE_CATEGORY_CREATION_FAILURE,
      },
      () => 'Failed to create category.',
    )
    .otherwise(() => UPSERT_MESSAGE_CATEGORY_ERROR_MESSAGE);
};

export const useUpsertMessageCategories = (options?: ManageMessageCategoriesOptions) => {
  const dispatch = useDispatch();

  const {
    failure,
    isLoading,
    manageMessageCategories: upsertMessageCategories,
    reset,
  } = useManageMessageCategories({
    ...options,
    onError: (error) => {
      const errorMessage = error?.message || UPSERT_MESSAGE_CATEGORY_ERROR_MESSAGE;
      dispatch(addNegativeToast(errorMessage));
    },
    onCompleted: (data) => {
      const _failure = data?.manageMessageCategories?.failure;

      if (_failure) {
        const message = failureToErrorMessage(_failure);
        dispatch(addNegativeToast(message));
      } else {
        if (data?.manageMessageCategories?.created?.length > 0) {
          const { created } = data.manageMessageCategories;
          const name = getManageMessageCategoriesNames(created);
          const message = `${pluralizeCategory(created)} successfully created.`;

          dispatch(addPositiveToast(<ToastMessage name={name} message={message} />));
        }

        if (data?.manageMessageCategories?.updated?.length > 0) {
          const { updated } = data.manageMessageCategories;
          const name = getManageMessageCategoriesNames(updated);
          const message = `${pluralizeCategory(updated)} successfully updated.`;

          dispatch(addPositiveToast(<ToastMessage name={name} message={message} />));
        }
      }
    },
  });

  return {
    failure,
    isLoading,
    reset,
    upsertMessageCategories,
  };
};
